import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  getParts,
  parseFormattedNumber,
  formatNumber,
  getNextCursorPosition,
  getDecimalSeparatorIndex,
} from './utils';
import {POSSIBLE_DECIMAL_SEPARATORS, POSSIBLE_MINUS_KEYS} from './constants';
import {resolveKeyDown, isStateResult} from './resolver';
import type {ResolverConfig} from './resolver';

type ComponentMode = 'controlled' | 'uncontrolled';

export type NumericFieldOptions = {
  value?: number | null;
  defaultValue?: number;
  onChange?: (value?: number) => void;
  locale: string;
  fractionalDigits: number;
  minimumFractionDigits?: number;
  forceMaximumFractionDigits?: boolean;
  autoFocus?: boolean;
  allowNegative?: boolean;
  toDisplayValue: (normalizedValue: number, fractionalDigits: number) => number;
  fromDisplayValue: (displayValue: number, fractionalDigits: number) => number;
  showEmptyFractionDigits?: boolean;
  onExtendedFractionDigitsShown?: () => void;
  onUnsafeValue?: (value: number) => void;
  /**
   * When true, fraction digits are shown after the input loses focus if the
   * field has a value. This mirrors the old CurrencyField "fractionalMode on
   * blur" behavior.
   */
  padFractionsOnBlur?: boolean;
  /**
   * When true, the locale's group separator (e.g. "," in en-US) is suppressed
   * in the keydown handler rather than being treated as an alternate decimal
   * entry. Use for fields that display group separators (e.g. CurrencyField)
   * to avoid ambiguity between "1,234" (thousands) and "1,23" (decimal).
   */
  suppressGroupSeparatorAsDecimal?: boolean;
  /**
   * When true, the hook will NOT revert the display when the controlled value
   * doesn't change after an onChange call.
   */
  disableControlledRejection?: boolean;
};

type DisplayState = {
  value: number | null;
  formattedValue: string;
  hasFractionalDigits: boolean;
};

const createEmptyDisplayState = (): DisplayState => ({
  value: null,
  formattedValue: '',
  hasFractionalDigits: false,
});

export type NumericFieldResult = {
  displayValue: string;
  numericValue: number | null;
  inputRef: React.RefObject<HTMLInputElement>;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onInput: (e: React.FormEvent<HTMLInputElement>) => void;
  formattedPlaceholder: string;
};

export function useNumericField({
  value: controlledValue,
  defaultValue,
  onChange = () => undefined,
  locale,
  fractionalDigits,
  minimumFractionDigits: minimumFractionDigitsProp,
  forceMaximumFractionDigits: forceMaximumFractionDigitsProp = false,
  autoFocus,
  allowNegative = false,
  toDisplayValue,
  fromDisplayValue,
  showEmptyFractionDigits = false,
  onExtendedFractionDigitsShown,
  onUnsafeValue,
  padFractionsOnBlur = false,
  suppressGroupSeparatorAsDecimal = false,
  disableControlledRejection = false,
}: NumericFieldOptions): NumericFieldResult {
  const minimumFractionDigits = minimumFractionDigitsProp ?? fractionalDigits;
  const {groupSeparator, decimalSeparator, minusSign} = useMemo(
    () => getParts(locale),
    [locale],
  );

  // Track internally whether the user has typed extended fraction digits.
  // This allows the hook to immediately reformat without waiting for a
  // parent re-render cycle.
  const [internalForceMax, setInternalForceMax] = useState(
    forceMaximumFractionDigitsProp,
  );
  const forceMaximumFractionDigits =
    forceMaximumFractionDigitsProp || internalForceMax;

  useEffect(() => {
    if (!forceMaximumFractionDigitsProp) {
      setInternalForceMax(false);
    }
  }, [forceMaximumFractionDigitsProp]);

  const componentMode: ComponentMode =
    controlledValue !== undefined ? 'controlled' : 'uncontrolled';

  const formatDisplayValue = useCallback(
    (
      normalizedValue: number,
      hasFractionalDigits: boolean,
      forcedZero = true,
    ) => {
      const effectiveMin = forceMaximumFractionDigits
        ? fractionalDigits
        : minimumFractionDigits;
      return formatNumber(toDisplayValue(normalizedValue, fractionalDigits), {
        locale,
        fractionalDigits: hasFractionalDigits ? fractionalDigits : 0,
        minimumFractionDigits: hasFractionalDigits ? effectiveMin : 0,
        noDecimals: !hasFractionalDigits,
        forcedZero,
      });
    },
    [
      locale,
      fractionalDigits,
      minimumFractionDigits,
      forceMaximumFractionDigits,
      toDisplayValue,
    ],
  );

  const formattedPlaceholder = useMemo(() => {
    return formatDisplayValue(0, true);
  }, [formatDisplayValue]);

  const inputRef = useRef<HTMLInputElement>(null);
  const pendingCursorRef = useRef<number | null>(null);

  // The ref may point to a wrapper element (e.g. when TextField forwards ref
  // to a FormField wrapper). This helper resolves the actual native <input>.
  const getInputElement = useCallback((): HTMLInputElement | null => {
    const el = inputRef.current;
    if (!el) {
      return null;
    }
    if (el.tagName === 'INPUT') {
      return el;
    }
    return (el.querySelector('input[type="text"]') ??
      el.querySelector('input')) as HTMLInputElement | null;
  }, []);

  const [displayState, setDisplayState] = useState<DisplayState>(() => {
    let initialValue: number | null =
      controlledValue !== undefined
        ? controlledValue ?? null
        : defaultValue ?? null;
    if (
      initialValue != null &&
      !allowNegative &&
      (initialValue < 0 || Object.is(initialValue, -0))
    ) {
      initialValue = Math.abs(initialValue);
    }

    if (initialValue === null) {
      return createEmptyDisplayState();
    }

    const displayVal = toDisplayValue(initialValue, fractionalDigits);
    const hasFractionalDigits = showEmptyFractionDigits || displayVal % 1 !== 0;
    const formattedValue = formatDisplayValue(
      initialValue,
      hasFractionalDigits,
    );

    return {
      value: initialValue,
      formattedValue,
      hasFractionalDigits,
    };
  });

  useLayoutEffect(() => {
    if (pendingCursorRef.current !== null) {
      const el = getInputElement();
      if (el) {
        const cursor = pendingCursorRef.current;
        el.setSelectionRange(cursor, cursor);
        pendingCursorRef.current = null;
      }
    }
  }, [displayState, getInputElement]);

  useLayoutEffect(() => {
    if (autoFocus) {
      const el = getInputElement();
      if (el) {
        el.setSelectionRange(0, 0);
      }
    }
  }, [autoFocus, getInputElement]);

  const prevControlledValueRef = useRef<number | null | undefined>(
    controlledValue,
  );

  // Keeps displayState in sync with the controlled value. Runs before paint so
  // neither branch produces a visible intermediate frame.
  useLayoutEffect(() => {
    if (componentMode === 'uncontrolled') {
      return;
    }

    const effectiveValue =
      controlledValue != null &&
      !allowNegative &&
      (controlledValue < 0 || Object.is(controlledValue, -0))
        ? Math.abs(controlledValue)
        : controlledValue;

    const prevValue = prevControlledValueRef.current;
    const valueChangedExternally = controlledValue !== prevValue;

    if (valueChangedExternally) {
      // External change: the parent updated the value prop. Reformat
      // displayState to reflect the new value.
      prevControlledValueRef.current = controlledValue;

      if (effectiveValue == null) {
        setDisplayState(createEmptyDisplayState());
        setInternalForceMax(false);
        pendingCursorRef.current = 0;
        return;
      }

      setDisplayState((prev) => {
        const isNewValue = effectiveValue !== prev.value;
        const hasFractionalDigits =
          showEmptyFractionDigits ||
          (isNewValue
            ? toDisplayValue(effectiveValue, fractionalDigits) % 1 !== 0
            : prev.hasFractionalDigits);
        const formattedValue = formatDisplayValue(
          effectiveValue,
          hasFractionalDigits,
        );

        const currentCursor = getInputElement()?.selectionStart ?? 0;
        pendingCursorRef.current = Math.min(
          currentCursor,
          formattedValue.length,
        );

        return {value: effectiveValue, formattedValue, hasFractionalDigits};
      });
    } else if (
      !disableControlledRejection &&
      displayState.value !== effectiveValue
    ) {
      // Parent rejected: the parent's onChange did not update the value prop
      // (e.g. enforcing a min/max). Revert the display back to the controlled value.
      pendingCursorRef.current = null;
      if (effectiveValue == null) {
        setDisplayState(createEmptyDisplayState());
        setInternalForceMax(false);
        return;
      }
      setDisplayState((prev) => {
        const hasFractionalDigits =
          showEmptyFractionDigits ||
          toDisplayValue(effectiveValue, fractionalDigits) % 1 !== 0;
        return {
          ...prev,
          value: effectiveValue,
          formattedValue: formatDisplayValue(
            effectiveValue,
            hasFractionalDigits,
          ),
          hasFractionalDigits,
        };
      });
    }
  }, [
    allowNegative,
    componentMode,
    controlledValue,
    disableControlledRejection,
    displayState.value,
    fractionalDigits,
    formatDisplayValue,
    getInputElement,
    showEmptyFractionDigits,
    toDisplayValue,
  ]);

  const displayStateRef = useRef(displayState);
  displayStateRef.current = displayState;

  const updateValue = useCallback(
    (
      {
        formattedValue,
        cursor,
        hasFractionalDigits,
      }: {
        formattedValue: string;
        cursor: number;
        hasFractionalDigits: boolean;
      },
      overrideForceMax?: boolean,
    ) => {
      const currentState = displayStateRef.current;
      if (formattedValue === '') {
        const valueChanged = currentState.value !== null;
        const emptyState = createEmptyDisplayState();
        setDisplayState(emptyState);
        setInternalForceMax(false);
        displayStateRef.current = emptyState;
        pendingCursorRef.current = 0;
        if (valueChanged) {
          onChange(undefined);
        }
        return;
      }

      const numericValue = parseFormattedNumber(
        formattedValue,
        locale,
        fractionalDigits,
        currentState.formattedValue,
        !suppressGroupSeparatorAsDecimal || groupSeparator === '.',
      );
      if (Number.isNaN(numericValue)) {
        return;
      }

      // Guard display values before normalization; very large decimal inputs
      // cannot be represented accurately.
      if (Math.abs(numericValue) > Number.MAX_SAFE_INTEGER) {
        return;
      }

      let normalizedValue = fromDisplayValue(numericValue, fractionalDigits);
      if (
        !allowNegative &&
        (normalizedValue < 0 || Object.is(normalizedValue, -0))
      ) {
        normalizedValue = Math.abs(normalizedValue);
      }
      if (Math.abs(normalizedValue) > Number.MAX_SAFE_INTEGER) {
        onUnsafeValue?.(
          normalizedValue < 0
            ? -Number.MAX_SAFE_INTEGER
            : Number.MAX_SAFE_INTEGER,
        );
        return;
      }

      const effectiveHasFractionalDigits =
        hasFractionalDigits || numericValue % 1 !== 0;
      const forcedZero = formattedValue.length > 0;

      let finalFormattedValue: string;
      if (overrideForceMax && !forceMaximumFractionDigits) {
        // When force-max is being activated in this same cycle, compute
        // the formatted value with max padding directly.
        finalFormattedValue = formatNumber(
          toDisplayValue(normalizedValue, fractionalDigits),
          {
            locale,
            fractionalDigits: effectiveHasFractionalDigits
              ? fractionalDigits
              : 0,
            minimumFractionDigits: effectiveHasFractionalDigits
              ? fractionalDigits
              : 0,
            noDecimals: !effectiveHasFractionalDigits,
            forcedZero,
          },
        );
      } else {
        finalFormattedValue = formatDisplayValue(
          normalizedValue,
          effectiveHasFractionalDigits,
          forcedZero,
        );
      }

      let finalCursor = getNextCursorPosition(
        formattedValue,
        finalFormattedValue,
        cursor,
        locale,
      );
      if (
        formattedValue.startsWith('0') &&
        formattedValue.slice(1) === finalFormattedValue &&
        currentState.value === normalizedValue
      ) {
        finalCursor = 0;
      }

      const valueChanged = currentState.value !== normalizedValue;
      const nextState = {
        value: normalizedValue,
        formattedValue: finalFormattedValue,
        hasFractionalDigits: effectiveHasFractionalDigits,
      };
      setDisplayState(nextState);
      displayStateRef.current = nextState;
      pendingCursorRef.current = finalCursor;

      if (valueChanged) {
        onChange(normalizedValue);
      }
    },
    [
      allowNegative,
      locale,
      fractionalDigits,
      fromDisplayValue,
      formatDisplayValue,
      forceMaximumFractionDigits,
      toDisplayValue,
      onChange,
      onUnsafeValue,
      groupSeparator,
      suppressGroupSeparatorAsDecimal,
    ],
  );

  const config: ResolverConfig = useMemo(
    () => ({
      locale,
      fractionalDigits,
      groupSeparator,
      decimalSeparator,
      minusSign,
      allowNegative,
      showEmptyFractionDigits,
      forceMaximumFractionDigits,
      suppressGroupSeparatorAsDecimal,
    }),
    [
      locale,
      fractionalDigits,
      groupSeparator,
      decimalSeparator,
      minusSign,
      allowNegative,
      showEmptyFractionDigits,
      forceMaximumFractionDigits,
      suppressGroupSeparatorAsDecimal,
    ],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.metaKey || e.ctrlKey || e.altKey) {
        return;
      }

      const currentState = displayStateRef.current;
      const selectionStart = e.currentTarget.selectionStart ?? 0;
      const selectionEnd = e.currentTarget.selectionEnd ?? selectionStart;

      const result = resolveKeyDown(
        {
          formattedValue: currentState.formattedValue,
          numericValue: currentState.value,
          hasFractionalDigits: currentState.hasFractionalDigits,
        },
        e.key,
        [selectionStart, selectionEnd],
        config,
      );

      if (result === null) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      if (result === 'suppress') {
        return;
      }

      if (isStateResult(result)) {
        const isDecimalInitSetup =
          POSSIBLE_DECIMAL_SEPARATORS.has(e.key) &&
          (currentState.value === null || currentState.value === 0) &&
          (currentState.formattedValue === '' ||
            currentState.formattedValue === '0' ||
            (selectionStart === 0 &&
              selectionEnd === currentState.formattedValue.length));

        const isMinusInitSetup =
          (POSSIBLE_MINUS_KEYS.has(e.key) || e.key === minusSign) &&
          currentState.value === null &&
          currentState.formattedValue === '';

        // When minimumFractionDigits equals fractionalDigits, all fraction digits
        // are "extended". Notify immediately since onInput won't fire (the resolver
        // handles the keystroke entirely).
        const isExtendedFractionActivation =
          onExtendedFractionDigitsShown != null &&
          POSSIBLE_DECIMAL_SEPARATORS.has(e.key) &&
          result.hasFractionalDigits &&
          minimumFractionDigits === fractionalDigits &&
          fractionalDigits > 0;

        if (isExtendedFractionActivation) {
          setInternalForceMax(true);
          onExtendedFractionDigitsShown!();
        }

        if (isDecimalInitSetup || isMinusInitSetup) {
          setDisplayState({
            value: isMinusInitSetup ? null : 0,
            formattedValue: result.formattedValue,
            hasFractionalDigits: result.hasFractionalDigits,
          });
          pendingCursorRef.current = result.cursor;
        } else {
          updateValue(result, isExtendedFractionActivation);
        }

        // Synchronously update the DOM so the next keystroke (which may
        // arrive before React commits) sees the correct value and cursor.
        const inputEl = getInputElement();
        if (inputEl) {
          inputEl.value = result.formattedValue;
          inputEl.setSelectionRange(result.cursor, result.cursor);
        }
      } else {
        const inputEl = getInputElement();
        inputEl?.setSelectionRange(result.cursor, result.cursor);
      }
    },
    [
      config,
      updateValue,
      getInputElement,
      minusSign,
      minimumFractionDigits,
      fractionalDigits,
      onExtendedFractionDigitsShown,
    ],
  );

  const onInput = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      const targetEl = e.target as HTMLInputElement | undefined;
      const changedValue = targetEl?.value ?? e.currentTarget.value;
      const cursor =
        targetEl?.selectionEnd ?? e.currentTarget.selectionEnd ?? 0;

      // Check for fractional digits: either the native decimal separator is
      // present, or the group separator is being used as an alternate decimal.
      // Compute this first so the shouldForceMax block can use hasAlternateDecimal
      // to handle iOS keyboards that send the group separator as a decimal.
      const hasNativeDecimal =
        getDecimalSeparatorIndex(changedValue, locale) !== -1;
      let hasAlternateDecimal = false;
      const previousFormattedValue = displayStateRef.current.formattedValue;
      const previousHadGroup = previousFormattedValue.includes(groupSeparator);
      if (
        !hasNativeDecimal &&
        !previousHadGroup &&
        groupSeparator !== decimalSeparator &&
        fractionalDigits > 0
      ) {
        const canUseGroupSeparatorAsDecimal =
          !suppressGroupSeparatorAsDecimal || groupSeparator === '.';
        const sepCount = (
          changedValue.match(
            new RegExp(groupSeparator.replace('.', '\\.'), 'g'),
          ) || []
        ).length;
        if (sepCount === 1) {
          const sepIdx = changedValue.indexOf(groupSeparator);
          const after = changedValue.slice(sepIdx + groupSeparator.length);
          hasAlternateDecimal =
            canUseGroupSeparatorAsDecimal &&
            (after.length === 0 || (/^\d+$/.test(after) && after.length !== 3));
        }
      }

      // Check if the user typed past the base minimum fraction digits.
      // When minimumFractionDigits equals fractionalDigits, ALL fraction digits
      // are "extended" (e.g. JPY with fractionDigitOffset), so any typed digit
      // past the decimal counts.
      // Also handles iOS cross-locale input: when the native decimal separator
      // isn't found but the group separator is acting as an alternate decimal,
      // derive the decimal index from the alternate position.
      let shouldForceMax = false;
      if (onExtendedFractionDigitsShown && !forceMaximumFractionDigits) {
        let decIdx = getDecimalSeparatorIndex(changedValue, locale);
        if (decIdx === -1 && hasAlternateDecimal) {
          decIdx = changedValue.indexOf(groupSeparator);
        }
        if (decIdx !== -1) {
          const digitsAfterDecimal = changedValue
            .slice(decIdx + 1)
            .replace(/\D/g, '').length;
          const threshold =
            minimumFractionDigits === fractionalDigits
              ? 0
              : minimumFractionDigits;
          if (digitsAfterDecimal > threshold) {
            shouldForceMax = true;
            setInternalForceMax(true);
            onExtendedFractionDigitsShown();
          }
        }
      }

      updateValue(
        {
          formattedValue: changedValue,
          cursor,
          hasFractionalDigits:
            showEmptyFractionDigits || hasNativeDecimal || hasAlternateDecimal,
        },
        shouldForceMax,
      );
    },
    [
      locale,
      updateValue,
      showEmptyFractionDigits,
      minimumFractionDigits,
      fractionalDigits,
      forceMaximumFractionDigits,
      onExtendedFractionDigitsShown,
      decimalSeparator,
      groupSeparator,
      suppressGroupSeparatorAsDecimal,
    ],
  );

  // When showEmptyFractionDigits becomes true after initialization (e.g. on
  // blur in CurrencyField), reformat the display to include fraction digits.
  // Also reformat when formatDisplayValue changes (e.g. when
  // forceMaximumFractionDigits or minimumFractionDigits change).
  const prevShowEmptyRef = useRef(showEmptyFractionDigits);
  const prevFormatDisplayValueRef = useRef(formatDisplayValue);
  useLayoutEffect(() => {
    const wasShowing = prevShowEmptyRef.current;
    const prevFormat = prevFormatDisplayValueRef.current;
    prevShowEmptyRef.current = showEmptyFractionDigits;
    prevFormatDisplayValueRef.current = formatDisplayValue;

    const current = displayStateRef.current;
    if (current.value === null) {
      return;
    }

    const showEmptyChanged = showEmptyFractionDigits && !wasShowing;
    const formatChanged = formatDisplayValue !== prevFormat;

    if (showEmptyChanged && !current.hasFractionalDigits) {
      const fv = formatDisplayValue(current.value, true);
      setDisplayState({
        value: current.value,
        formattedValue: fv,
        hasFractionalDigits: true,
      });
    } else if (formatChanged) {
      const fv = formatDisplayValue(current.value, current.hasFractionalDigits);
      if (fv !== current.formattedValue) {
        setDisplayState({
          value: current.value,
          formattedValue: fv,
          hasFractionalDigits: current.hasFractionalDigits,
        });
      }
    }
  }, [showEmptyFractionDigits, formatDisplayValue]);

  useEffect(() => {
    const el = getInputElement();

    if (!el) {
      return;
    }

    const handleBlur = () => {
      const current = displayStateRef.current;

      if (allowNegative && Object.is(current.value, -0)) {
        const fv = formatDisplayValue(0, current.hasFractionalDigits);

        setDisplayState({
          value: 0,
          formattedValue: fv,
          hasFractionalDigits: current.hasFractionalDigits,
        });

        pendingCursorRef.current = 0;
        return;
      }

      // On blur, show fraction digits if there's a value but fractions aren't
      // currently displayed (mirrors the old CurrencyField fractionalMode behavior).
      if (
        padFractionsOnBlur &&
        fractionalDigits > 0 &&
        current.value !== null &&
        !current.hasFractionalDigits
      ) {
        const fv = formatDisplayValue(current.value, true);
        setDisplayState({
          value: current.value,
          formattedValue: fv,
          hasFractionalDigits: true,
        });
        pendingCursorRef.current = null;
      }
    };

    el.addEventListener('blur', handleBlur);

    return () => el.removeEventListener('blur', handleBlur);
  }, [
    allowNegative,
    fractionalDigits,
    formatDisplayValue,
    getInputElement,
    padFractionsOnBlur,
  ]);

  const numericValue =
    displayState.value != null && Object.is(displayState.value, -0)
      ? 0
      : displayState.value;

  return {
    displayValue: displayState.formattedValue,
    numericValue,
    inputRef,
    onKeyDown,
    onInput,
    formattedPlaceholder,
  };
}
